"use client";

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { toast } from "sonner";
import { Loader2, CheckCircle2, ExternalLink } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";

import { useSellerId } from "@/hooks/useSellerId";
import { useCurrentUser } from "@/hooks/useCurrentUser";

type StripeConnectButtonProps = {
  redirectTo?: string
  context?: 'funnels' | 'settings'
}

export function StripeConnectButton({
  redirectTo = '/settings/payment_methods',
  context = 'settings',
}: {
  redirectTo?: string
  context?: 'funnels' | 'settings'
}) {
  const { sellerId } = useSellerId();
  const { user, userId, loggedIn } = useCurrentUser();

  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<string>("");
  const [selectedAccountType, setSelectedAccountType] = useState<'express' | 'standard'>('express');

  const [sellerAccount, setSellerAccount] = useState<any>(null);

  useEffect(() => {
    if (selectedCountry === 'MX') {
      setSelectedAccountType('standard')
    }
  }, [selectedCountry])

  // ✅ Cargar estado Stripe del usuario logueado
  useEffect(() => {
    const loadAccount = async () => {
      if (!userId) {
        setIsLoading(false);
        return;
      }

      try {
        const res = await fetch(`/api/stripe/check-account?user_id=${userId}`);
        const data = await res.json();

        if (res.ok && data) {
          // ahora data ya trae directamente las propiedades
          setSellerAccount(data);
        } else {
          console.warn("Stripe account not found for user:", data?.error);
        }
      } catch (err) {
        console.error("Error loading Stripe account:", err);
      } finally {
        setIsLoading(false);
      }
    };
    loadAccount();
  }, [userId]);

  useEffect(() => {
    const onFocus = () => {
      if (userId) {
        fetch(`/api/stripe/check-account?user_id=${userId}`)
          .then(res => res.json())
          .then(data => {
            if (data?.accountId) {
              setSellerAccount(data);
            }
          })
          .catch(() => {});
      }
    };

    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [userId]);


  // 🔹 Crear cuenta nueva si no existe
  const handleConnect = async () => {
    if (!selectedCountry) {
      toast.error("Por favor selecciona tu país");
      return;
    }

    if (!loggedIn || !userId) {
      toast.error("Debes iniciar sesión para conectar tu Stripe");
      return;
    }

    setIsConnecting(true);
    try {
      const res = await fetch("/api/stripe/create-stripe-account", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId,
          email: user?.email,
          country: selectedCountry,
          accountType: selectedAccountType,
          businessName: user?.firstname || "Mi negocio",
          redirectTo,
          context,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Error creando cuenta de Stripe");

      if (data.onboardingUrl) {
        window.open(data.onboardingUrl, "_blank");
        toast.success("Abre la ventana para completar el onboarding de Stripe");
      } else {
        toast.error("No se recibió la URL de onboarding");
      }

    } catch (error: any) {
      console.error("Stripe connect error:", error);
      toast.error(error.message || "Error al conectar con Stripe");
    } finally {
      setIsConnecting(false);
    }
  };

  // 🔹 Verificar / actualizar estado de cuenta
  const handleCheckStatus = async () => {
    if (!sellerAccount?.accountId) {
      toast.error("No se encontró una cuenta Stripe para verificar");
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch("/api/stripe/check-account", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          accountId: sellerAccount.accountId,
          user_id: userId,
        }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Error verificando cuenta");

      setSellerAccount(data);
      toast.success("Estado de cuenta actualizado");
    } catch (err: any) {
      console.error("Error checking account:", err);
      toast.error(err.message || "Error al verificar cuenta");
    } finally {
      setIsLoading(false);
    }
  };

  // 🔹 Estado visual
  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin" />
        </CardContent>
      </Card>
    );
  }

  // 🔹 Condición mejorada: incluye onboarding_completed
  const isFullyConnected = Boolean(sellerAccount?.accountId);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Stripe Connect
          {isFullyConnected && (
            <CheckCircle2 className="h-5 w-5 text-green-500" />
          )}
        </CardTitle>
        <CardDescription>
          Conecta tu cuenta de Stripe para recibir pagos directamente
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* 🔹 Si ya está conectada y completa, muestra info */}
        {isFullyConnected ? (
          <>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Estado:</span>
                <span className="text-green-600 font-medium">Conectado</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Cuenta:</span>
                <span>{sellerAccount.accountId}</span>
              </div>
              {sellerAccount.account_type && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Tipo:</span>
                  <span className="uppercase">{sellerAccount.account_type}</span>
                </div>
              )}
              {sellerAccount.business_name && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Negocio:</span>
                  <span>{sellerAccount.business_name}</span>
                </div>
              )}
              {sellerAccount.email && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Email:</span>
                  <span>{sellerAccount.email}</span>
                </div>
              )}
            </div>

            <Button
              variant="outline"
              onClick={handleCheckStatus}
              className="w-full"
            >
              Actualizar Estado
            </Button>
          </>
        ) : (
          <>
            {/* 🔹 Mostrar formulario si aún no está conectado */}
            <p className="text-sm text-muted-foreground mb-4">
              Para recibir pagos, necesitas conectar una cuenta de Stripe.
            </p>

            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="country">País de tu negocio</Label>
                <Select
                  value={selectedCountry}
                  onValueChange={setSelectedCountry}
                >
                  <SelectTrigger id="country">
                    <SelectValue placeholder="Selecciona tu país" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MX">México</SelectItem>
                    <SelectItem value="US">Estados Unidos</SelectItem>
                    <SelectItem value="CO">Colombia</SelectItem>
                    <SelectItem value="AR">Argentina</SelectItem>
                    <SelectItem value="CL">Chile</SelectItem>
                    <SelectItem value="PE">Perú</SelectItem>
                    <SelectItem value="BR">Brasil</SelectItem>
                    <SelectItem value="ES">España</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Este país no se puede cambiar después de crear la cuenta.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="account-type">Tipo de cuenta Stripe</Label>
                <Select
                  value={selectedAccountType}
                  onValueChange={(v) => setSelectedAccountType(v as 'express' | 'standard')}
                >
                  <SelectTrigger id="account-type">
                    <SelectValue placeholder="Selecciona el tipo de cuenta" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="express">Express</SelectItem>
                    <SelectItem value="standard">Standard</SelectItem>
                  </SelectContent>
                </Select>

                <p className="text-xs text-muted-foreground">
                  Express = onboarding rápido y más control de la plataforma. Standard = control total del seller en Stripe.
                </p>
              </div>

              <Button
                onClick={handleConnect}
                disabled={isConnecting || !selectedCountry}
                className="w-full"
              >
                {isConnecting ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Conectando...
                  </>
                ) : (
                  <>
                    <ExternalLink className="mr-2 h-4 w-4" />
                    Conectar Stripe
                  </>
                )}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
